import { AKComponent } from '@theme/component';
import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { FloatingPanelOpenedEvent } from '@theme/events';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 * 
 * This component:
 * - Positions itself to avoid viewport overflow
 * - Automatically adjusts position when content changes
 * - Handles window resize events
 * - Optionally closes parent details on resize
 * - Integrates with the theme's view transition system
 * - Follows AK component patterns and conventions
 */
export class FloatingPanelComponent extends AKComponent {
  constructor() {
    super();
    this.mutationObserver = null;
    this.resizeListener = null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Set up debounced resize listener
    this.resizeListener = debounce(this.onResize.bind(this), 100);
    window.addEventListener('resize', this.resizeListener, { passive: true });

    // Set up click outside listener
    this.clickOutsideListener = this.onClickOutside.bind(this);
    document.addEventListener('click', this.clickOutsideListener);

    // Set up keyboard listener for Escape key
    this.keydownListener = this.onKeydown.bind(this);
    document.addEventListener('keydown', this.keydownListener);

    // Set up toggle listener on parent details to dispatch floating panel opened event
    const parentDetails = this.closest('details');
    if (parentDetails) {
      this.toggleListener = this.onToggle.bind(this);
      parentDetails.addEventListener('toggle', this.toggleListener);
    }

    // Use requestIdleCallback to defer initial positioning for better performance
    requestIdleCallback(() => {
      this.updatePosition();
      this.setupMutationObserver();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }

    if (this.clickOutsideListener) {
      document.removeEventListener('click', this.clickOutsideListener);
      this.clickOutsideListener = null;
    }

    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = null;
    }

    if (this.toggleListener) {
      const parentDetails = this.closest('details');
      if (parentDetails) {
        parentDetails.removeEventListener('toggle', this.toggleListener);
      }
      this.toggleListener = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  /**
   * Sets up mutation observer to watch for content changes
   */
  setupMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    
    this.mutationObserver = new MutationObserver(() => {
      this.updatePosition();
    });
    
    this.mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Updates the panel position to avoid viewport overflow
   */
  updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition?.current) {
      await viewTransition.current;
    }

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Set top position with offset
    this.style.top = OFFSET + 'px';

    // Handle horizontal overflow - right side
    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    // Handle horizontal overflow - left side
    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    // Clear any recorded mutations
    if (this.mutationObserver) {
      this.mutationObserver.takeRecords();
    }
  };

  /**
   * Handles window resize events
   */
  onResize = () => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
    }
    
    this.updatePosition();
  };

  /**
   * Handles keyboard events
   * @param {KeyboardEvent} event
   */
  onKeydown = (event) => {
    // Close on Escape key
    if (event.key === 'Escape') {
      const parentDetails = this.closest('details');
      if (parentDetails && parentDetails.open) {
        parentDetails.removeAttribute('open');

        // Return focus to the summary element
        const summary = parentDetails.querySelector('summary');
        if (summary) {
          summary.focus();
        }
      }
    }
  };

  /**
   * Handles toggle events on parent details element
   * Dispatches floating panel opened event when opened
   */
  onToggle = () => {
    const parentDetails = this.closest('details');

    // If this details is being opened, dispatch event
    if (parentDetails && parentDetails.open) {
      // Use requestAnimationFrame to ensure DOM is updated before dispatching
      requestAnimationFrame(() => {
        document.dispatchEvent(new FloatingPanelOpenedEvent(parentDetails));
      });
    }
  };

  /**
   * Handles clicks outside the component to close details
   * @param {MouseEvent} event
   */
  onClickOutside = (event) => {
    const target = event.target;

    // Check if target is a Node (required for contains method)
    if (!(target instanceof Node)) {
      return;
    }

    // Find the parent details element of this floating panel
    const parentDetails = this.closest('details');
    if (!parentDetails) {
      return;
    }

    // If click is inside the parent details (including summary and floating panel), don't close
    // Let the browser handle the native details toggle behavior
    if (parentDetails.contains(target)) {
      return;
    }

    // Close the parent details since click is outside
    parentDetails.removeAttribute('open');
  };
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}